/**
 * Rendi cart drawer behaviour (Figma "Cart Sheet").
 *
 * One client-only detail the Liquid can't render:
 *  - Urgency countdown: a persistent "reserved for mm:ss" timer kept in
 *    localStorage so it keeps ticking across reloads and AJAX cart merges.
 *    The callout lives outside the [data-merge] header, so a single interval
 *    survives merges; we just reveal it and update [data-rendi-timer-display].
 *
 * The Rendi Plus express activation lives in rendi-cart-plus.js.
 */
(function () {
  'use strict';

  const TIMER_KEY = 'rendi:cart-deadline';

  // Translated strings ride data-t-* attributes on the drawer root (set in
  // cart-drawer.liquid), which the AJAX merges never touch. [TITLE] is the
  // interpolation placeholder, same convention as theme.strings.
  function t(name, fallback) {
    const drawer = document.querySelector('.js-cart-drawer');
    const value = drawer && drawer.dataset['t' + name];
    return value || fallback;
  }

  /* ---- Urgency countdown ------------------------------------------------ */

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function timerEl() {
    return document.querySelector('[data-rendi-timer]');
  }

  // Stored deadline, refreshed to a new window once it lapses so the
  // reservation always reads live.
  function deadlineFor(minutes) {
    const now = Date.now();
    let stored = parseInt(localStorage.getItem(TIMER_KEY), 10);
    if (!stored || stored <= now) {
      stored = now + minutes * 60000;
      try {
        localStorage.setItem(TIMER_KEY, String(stored));
      } catch (e) {
        /* private mode: timer still runs for this page */
      }
    }
    return stored;
  }

  function tick() {
    const el = timerEl();
    if (!el) return;
    const minutes = parseInt(el.getAttribute('data-minutes'), 10) || 10;
    let remaining = deadlineFor(minutes) - Date.now();
    if (remaining < 0) remaining = 0;
    const total = Math.floor(remaining / 1000);
    const out = el.querySelector('[data-rendi-timer-display]');
    if (out) out.textContent = Math.floor(total / 60) + ':' + pad(total % 60);
    el.hidden = false;
  }

  function startTimer() {
    if (!timerEl()) return;
    tick();
    if (!window.__rendiCartTimer) {
      window.__rendiCartTimer = setInterval(tick, 1000);
    }
  }

  /* ---- Removal toast + undo --------------------------------------------- */

  // When a line is removed the theme flips its quantity to 0 and re-renders the
  // drawer; we show the Figma "Sonner" toast near the footer with an Undo that
  // re-adds the line. The row data is read on the same click, before the merge
  // wipes the row: the theme writes value=0 synchronously, so we read the
  // exact line data exposed on the row instead of the live quantity input, which
  // the theme has already changed to zero. This preserves selling plans and all
  // public/private line properties when Undo re-adds the item.
  let activeToast = null;

  function showCartToast(options) {
    const region = document.querySelector('.js-cart-drawer [data-rendi-cart-toast]');
    if (!region || !window.RendiToast) return;
    if (activeToast) activeToast.dismiss(); // one toast at a time
    options.theme = 'inverted';
    options.container = region;
    activeToast = window.RendiToast.show(options);
  }

  function showRemovalToast(title, line) {
    showCartToast({
      message: t('Removed', '"[TITLE]" rimosso').replace('[TITLE]', title),
      action: {
        label: t('Undo', 'Annulla'),
        onClick: function () {
          const body = { items: [line] };
          window.RendiCart.mutate('add', body).catch(function () {});
        }
      }
    });
  }

  function captureRemoval(removeLink) {
    const row = removeLink.closest('.cart-item');
    if (!row) return;
    const titleEl = row.querySelector('.rendi-cart-item__title');
    const title = titleEl ? titleEl.textContent.trim() : t('Item', 'Articolo');
    const variantId = row.dataset.rendiVariantId;
    if (!variantId) return;

    const line = {
      id: variantId,
      quantity: parseInt(row.dataset.rendiQuantity, 10) || 1
    };
    const sellingPlanId = row.dataset.rendiSellingPlanId;
    if (sellingPlanId) line.selling_plan = sellingPlanId;
    try {
      const properties = JSON.parse(row.dataset.rendiProperties || '{}');
      if (properties && Object.keys(properties).length) line.properties = properties;
    } catch (e) {
      // Malformed row data should not prevent restoring the base line.
    }
    showRemovalToast(title, line);
  }

  // Order note sits at the very bottom of the scroll area; revealing or focusing
  // it doesn't auto-scroll, so it lands below the fold. Bring it into view.
  // offsetHeight is the reliable open signal: the theme keeps toggle-target--hidden
  // on until the reveal transition ends, but height is > 0 as soon as it opens
  // (and back to 0 once collapsed, so this no-ops on close).
  function scrollNoteIntoView() {
    const note = document.querySelector('.js-cart-drawer #CartDrawerNote');
    if (!note || !note.offsetHeight) return;
    note.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }

  /* ---- Bundle discount (Bundler) ---------------------------------------- */

  // Some bundles Bundler applies only at checkout, so cart.total_price never
  // sees them and the Liquid summary below the bundle row has to be rebuilt on
  // top of Bundler's number. At checkout the credit Function does run, but on
  // the bundle-discounted lines, so the credit scales with them and stays
  // capped by the customer balance. Bundles already in the cart are left alone
  // (syncBundleTotals).
  // Locale-agnostic: keep digits and separators, read a trailing ",dd"/".dd"
  // group as the decimal part and drop every other separator, so the parse
  // survives a shop money_format that groups the other way around.
  function parseMoney(text) {
    const raw = text.replace(/[^\d.,]/g, '');
    if (!raw) return 0;
    const decimals = raw.match(/[.,](\d{2})$/);
    const units = (decimals ? raw.slice(0, -3) : raw).replace(/[.,]/g, '');
    return (parseInt(units, 10) || 0) * 100 + (decimals ? parseInt(decimals[1], 10) : 0);
  }

  function formatMoney(cents) {
    const parts = (cents / 100).toFixed(2).split('.');
    const amount = parts[0].replace(/\B(?=(\d{3})+$)/g, '.') + ',' + parts[1];
    return (window.money_format || '€{{amount_with_comma_separator}}').replace(/\{\{.*?\}\}/, amount);
  }

  // Skipping identical writes keeps the summary observer from re-entering.
  function setMoney(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  // Both free-shipping measures in the drawer (this bar, the summary's "Gratis"
  // row) are rendered from the cart total, which never sees the Bundler
  // discount: with a bundle they read high and would promise a free shipping
  // the checkout then denies. Both are redrawn here on the net total this
  // script already rescales. Only the shipping tier moves:
  // the gift ones are left to the checkout.
  // Reversible on its own: mergeNodes keeps the existing HTML when the merge
  // cache key is unchanged, so the Liquid re-render can't be the only way back.
  // Stashing the markup is cheaper than rebuilding it, and it can't go stale: a
  // merge that does run replaces these nodes, dataset included.
  function stash(el, on) {
    if (on) {
      if (el.dataset.shipStash === undefined) el.dataset.shipStash = el.innerHTML;
      return true;
    }
    if (el.dataset.shipStash === undefined) return false;
    el.innerHTML = el.dataset.shipStash;
    delete el.dataset.shipStash;
    return false;
  }

  // base = the shipping base in cents. The bundle can only lower it, so a tier
  // Liquid already painted as reached can drop out, never the reverse — which
  // is why the summary row and the lead line only ever need swapping one way.
  function redrawShipping(base) {
    const row = document.querySelector('.js-cart-drawer [data-rendi-shipping]');
    if (row) {
      const threshold = parseInt(row.dataset.rendiShippingThreshold, 10) || 0;
      if (threshold > 0 && stash(row, base < threshold)) {
        const free = row.querySelector('.rendi-cart__sum-free');
        if (free) {
          free.className = 'rendi-cart__sum-value rendi-cart__sum-value--muted';
          free.textContent = row.dataset.rendiShippingUnknown;
        }
      }
    }
    const bar = document.querySelector('.js-cart-drawer [data-rendi-ship-progress]');
    if (!bar) return;
    const threshold = parseInt(bar.dataset.rendiShipThreshold, 10) || 0;
    if (threshold <= 0) return;
    const done = base >= threshold;
    const step = bar.querySelector('[data-rendi-ship-step]');
    if (step) {
      const fill = step.querySelector('.rendi-ship__fill');
      if (fill) {
        // same linear fill as rendi-ship-fill.liquid, on lower = 0
        fill.style.width = Math.min(100, Math.max(0, Math.floor((base * 100) / threshold))) + '%';
        fill.classList.toggle('rendi-ship__fill--done', done);
      }
      const label = step.querySelector('.rendi-ship__label');
      // the label ships both icons, CSS picks the one matching this class
      if (label) label.classList.toggle('rendi-ship__label--done', done);
    }
    const lead = bar.querySelector('[data-rendi-ship-lead]');
    if (!lead) return;
    if (lead.dataset.shipLeadDone === undefined) {
      lead.dataset.shipLeadDone = lead.classList.contains('rendi-ship__text--done') ? '1' : '0';
    }
    lead.classList.toggle('rendi-ship__text--done', done && lead.dataset.shipLeadDone === '1');
    if (!stash(lead, !done)) return;
    // Below the threshold the lead line is the shipping one whatever Liquid
    // decided: a gift line would be pointing at the next reward while the
    // shipping bar it sits on just went backwards.
    const template = bar.dataset.rendiShipLeadTemplate;
    if (!template) return;
    // Liquid's rate and headroom for this render (rendi-ship-progress): an
    // estimate once a bundle has moved the credit, like the rest of the lead.
    const rate = parseInt(bar.dataset.rendiShipLeadRate, 10) || 0;
    const headroom = parseInt(bar.dataset.rendiShipLeadHeadroom, 10) || 0;
    const missingPaid = threshold - base;
    const missing = Math.min(Math.ceil((missingPaid * 100) / (100 - rate)), missingPaid + headroom);
    const html = template.replace('__AMOUNT__', formatMoney(missing));
    if (lead.innerHTML !== html) lead.innerHTML = html;
  }

  // A bundle Bundler applies as a discount code is already on the lines, and its
  // hook then holds the cart's whole discount. Line discounts are the positive
  // signal; the equality keeps out a checkout bundle next to other ones.
  function bundleAlreadyInCart(amount, cart) {
    return cart.original > cart.subtotal && amount === cart.original - cart.total;
  }

  // Cents a Bundler bundle will still take off at checkout. 0 with no hook, with
  // a broken bundle (Bundler hides its row but leaves the amount in the hook) and
  // with a bundle already in the cart, whose row this hides: the subtotal shows it.
  function readBundle(cart) {
    const hook = document.querySelector('.js-cart-drawer .bundler-cart-discount-value-no-minus');
    if (!hook) return 0;
    const row = hook.closest('.bundler-cart-show-on-discount');
    if (row && row.style.display === 'none') return 0;
    const amount = parseMoney(hook.textContent);
    if (amount > 0 && bundleAlreadyInCart(amount, cart)) {
      if (row) row.style.display = 'none';
      return 0;
    }
    return amount;
  }

  // The Plus callout prints the summary's credit row, copied rather than
  // rendered: a merge that skips the callout's region can't split the two.
  function mirrorPlusCredit() {
    const row = document.querySelector('.js-cart-drawer [data-rendi-credit]');
    const callout = document.querySelector('.js-cart-drawer [data-rendi-plus-credit]');
    if (row && callout) setMoney(callout, row.textContent.replace('−', '').trim());
  }

  function syncBundleTotals() {
    rescaleForBundle();
    mirrorPlusCredit();
  }

  function rescaleForBundle() {
    const totalEl = document.querySelector('.js-cart-drawer [data-rendi-total]');
    if (!totalEl) return;
    const d = totalEl.dataset;
    const cents = (name) => parseInt(d[name], 10) || 0;
    const original = cents('original');
    const cart = { original, subtotal: cents('subtotal'), total: cents('total') };
    const bundle = readBundle(cart);
    // No bundle and nothing of ours left over: Liquid already rendered the right
    // numbers, don't round-trip them through parse/format.
    if (bundle <= 0 && !d.bundleAdjusted) return;
    if (bundle > 0) {
      d.bundleAdjusted = '1';
    } else {
      delete d.bundleAdjusted;
    }
    const credit = cents('credit');
    const potential = cents('creditPotential');
    const balance = cents('creditBalance');
    // whatever else the cart already discounts (codes, RENDIGIFT): unaffected
    // by the bundle, so it carries over untouched
    const other = original - cart.total - credit;
    // Contract: this linear rescale approximates the per-line algorithm of the
    // Function. With credit-ineligible lines next to a bundle the drawer drifts
    // a few cents from checkout (which stays authoritative), and the per-line
    // chips still split the un-rescaled credit.
    const scaled = credit > 0 && original > 0
      ? Math.min(Math.round((potential * (original - bundle)) / original), balance)
      : credit;
    // Math.max keeps a misparse from ever showing a negative total.
    const net = Math.max(0, original - bundle - other - scaled);
    const total = formatMoney(net);
    redrawShipping(net);
    setMoney(totalEl, total);
    setMoney(document.querySelector('.js-cart-drawer [data-rendi-credit]'), '−' + formatMoney(scaled));
    // The checkout CTA prints the same number and lives outside the summary,
    // so it has to follow: refill its translated label from the placeholder.
    const cta = document.querySelector('.js-cart-drawer [data-rendi-checkout]');
    if (cta && cta.dataset.rendiCheckoutLabel) {
      setMoney(cta.querySelector('.btn__text'), cta.dataset.rendiCheckoutLabel.replace('__TOTAL__', total));
    }
  }

  // The wrapper survives the AJAX merges (mergeNodes only swaps innerHTML), so
  // a single observer covers both the merge and Bundler's own writes.
  function watchSummary() {
    const summary = document.querySelector('.js-cart-drawer .rendi-cart__summary');
    if (!summary || summary.dataset.bundleWatched) return;
    summary.dataset.bundleWatched = '1';
    new MutationObserver(syncBundleTotals).observe(summary, {
      childList: true,
      subtree: true,
      characterData: true,
      // Bundler shows and hides its row through the inline style
      attributes: true,
      attributeFilter: ['style']
    });
  }

  /* ---- Wiring ----------------------------------------------------------- */

  const PROMO_COLLAPSED_KEY = 'rendi-cart-promo-collapsed';

  // The promo banner starts collapsed (badge + savings line) and the chevron
  // expands it; it's never fully dismissable. It lives inside a
  // [data-merge="plus-promo"] wrapper that the AJAX merges re-render, so the
  // state must be re-applied after each refresh. Session-scoped: it starts
  // collapsed again on the next visit.
  function applyPromoCollapsed() {
    const promo = document.querySelector('.js-cart-drawer .rendi-cart-promo');
    if (!promo) return;
    let collapsed = true;
    try { collapsed = sessionStorage.getItem(PROMO_COLLAPSED_KEY) !== '0'; } catch (e) {}
    promo.classList.toggle('rendi-cart-promo--collapsed', collapsed);
    const toggle = promo.querySelector('[data-rendi-promo-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      toggle.setAttribute('aria-label', collapsed ? t('PromoExpand', 'Espandi il banner Rendi Plus') : t('PromoCollapse', 'Comprimi il banner Rendi Plus'));
    }
  }

  // Rendi Plus sheet: the savings amount lives on the promo banner
  // (data-promo-amount, re-rendered on every merge); the sheet sits outside
  // the merge regions, so we mirror it here. The sheet trigger only exists
  // together with the banner, so with no banner we just re-hide the line.
  function syncPlusSheet() {
    const savings = document.querySelector('[data-rendi-plus-sheet-savings]');
    if (!savings) return;
    const promo = document.querySelector('.js-cart-drawer .rendi-cart-promo');
    const amount = promo && promo.getAttribute('data-promo-amount');
    const out = savings.querySelector('[data-rendi-plus-sheet-amount]');
    if (amount && out) {
      out.textContent = amount;
      savings.hidden = false;
    } else {
      savings.hidden = true;
    }
  }

  function refresh() {
    startTimer();
    watchSummary();
    applyPromoCollapsed();
    syncPlusSheet();
    // Added view: the [data-merge-list-item] wrappers are replaced on every AJAX
    // merge, so the marker must be re-applied; if the row is gone (item removed
    // or quantity zeroed) fall back to the normal cart view.
    const drawer = document.querySelector('.js-cart-drawer');
    if (drawer && drawer.classList.contains('rendi-cart--added') && !markAddedItem()) {
      setAdded(false);
    }
  }

  // Flip the whole drawer to the "Aggiunto al carrello" view (header swap, only
  // the just-added row, CTA block, alternate upsell title — all CSS driven by
  // .rendi-cart--added). The class lives on the drawer root, which the AJAX
  // merges never touch, so the flag survives them.
  function setAdded(on) {
    const drawer = document.querySelector('.js-cart-drawer');
    if (drawer) drawer.classList.toggle('rendi-cart--added', on);
  }

  let addedVariantId = null;

  // Mark the line-item row of the last-added variant so the CSS can hide the
  // others. Line item keys are "variantId:hash", hence the prefix match on the
  // quantity input's data-key. Returns whether the row was found.
  function markAddedItem() {
    document.querySelectorAll('[data-rendi-added-item]').forEach(function (el) {
      el.removeAttribute('data-rendi-added-item');
    });
    if (!addedVariantId) return false;
    const input = document.querySelector(
      '.js-cart-drawer .cart-item__quantity-input[data-key^="' + addedVariantId + ':"]'
    );
    const row = input && input.closest('[data-merge-list-item]');
    if (!row) return false;
    row.setAttribute('data-rendi-added-item', '');
    return true;
  }

  function init() {
    refresh();
    syncBundleTotals();
    // The theme dispatches a bubbling on:cart-drawer:after-open from SideDrawer.open(),
    // so a document-level listener covers every open without a DOM observer.
    document.addEventListener('on:cart:after-merge', refresh);
    document.addEventListener('on:cart-drawer:after-open', refresh);

    // "Aggiunto al carrello" view: shown ONLY when the drawer opens because an
    // item was just added; a plain cart-icon open shows the normal cart. The
    // state dies when the drawer closes (after-close), so the order in which
    // the two add paths fire on:cart:add vs the drawer open is irrelevant.
    // Guarded on afterAddToCart === 'drawer': with 'notification'/'page' the
    // add never opens the drawer, and the state must not linger until a later
    // cart-icon open.
    document.addEventListener('on:cart-drawer:after-close', function () {
      addedVariantId = null;
      setAdded(false);
    });
    document.addEventListener('on:cart:add', function (e) {
      // Quick-add from the drawer's own xsell strip: no "added" view. Reset it
      // too, or a prior added view would keep every other row (incl. the new
      // one) hidden. The new row appears above the fold-out of sight, so a
      // confirmation toast (same Sonner as removal) is the feedback instead.
      if (e.detail && e.detail.fromDrawer) {
        addedVariantId = null;
        setAdded(false);
        showCartToast({
          message: t('Added', '"[TITLE]" aggiunto al carrello').replace('[TITLE]', e.detail.productTitle || t('Item', 'Articolo'))
        });
        return;
      }
      const after = (window.theme && theme.settings && theme.settings.afterAddToCart) || 'drawer';
      if (after !== 'drawer') return;
      addedVariantId = (e.detail && e.detail.variantId) || null;
      setAdded(markAddedItem());
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      // Read the row before the theme removes it; don't preventDefault so the
      // theme still performs the actual removal.
      const removeLink = e.target.closest('.cart-item__remove');
      if (removeLink && removeLink.closest('.js-cart-drawer')) {
        captureRemoval(removeLink);
      }
      const viewCart = e.target.closest('[data-rendi-view-cart]');
      if (viewCart) {
        addedVariantId = null;
        setAdded(false);
        const content = document.querySelector('.js-cart-drawer .rendi-cart__content');
        if (content) content.scrollTop = 0;
        return;
      }
      // Expand by tapping anywhere on the collapsed banner; collapse only via the
      // chevron. When expanded the banner shows the "Scopri come" CTA, so a body
      // tap must fall through to it instead of collapsing the banner.
      const promo = e.target.closest('.js-cart-drawer .rendi-cart-promo');
      if (promo) {
        const collapsed = promo.classList.contains('rendi-cart-promo--collapsed');
        const onChevron = !!e.target.closest('[data-rendi-promo-toggle]');
        if (collapsed || onChevron) {
          try { sessionStorage.setItem(PROMO_COLLAPSED_KEY, collapsed ? '0' : '1'); } catch (err) {}
          applyPromoCollapsed();
        }
      }
      // Order-note toggler: the theme reveals the field with a height transition,
      // so wait for it to settle before scrolling (fallback if there's no transition).
      if (e.target.closest('#CartDrawerNoteToggle')) {
        const note = document.querySelector('.js-cart-drawer #CartDrawerNote');
        if (note) {
          const onEnd = function () {
            note.removeEventListener('transitionend', onEnd);
            scrollNoteIntoView();
          };
          note.addEventListener('transitionend', onEnd);
          setTimeout(onEnd, 400);
        }
      }
    });
    // Also cover focusing the textarea directly (tab/tap into an open field).
    document.addEventListener('focusin', function (e) {
      if (e.target.closest && e.target.closest('.js-cart-drawer #CartDrawerNote')) {
        scrollNoteIntoView();
      }
    });
  }

  // Bundler vs Selleasy on the localized markets.
  // Selleasy (lb-utils.js) calls bndlr.preventBundlerCheckout() on load and only
  // re-enables it from its own click listener, which it binds with the selector
  // "button[name='checkout'], input[name='checkout'], a[href='/checkout']".
  // The drawer CTA is an anchor to routes.root_url + '/checkout', so on /fr-fr
  // (and any other market prefix) Selleasy never binds, never re-enables Bundler,
  // and the click goes to checkout without the bundle discount. Re-enable it
  // ourselves, but only when Selleasy did not claim the button (no attribute), so
  // the Italian flow with its pre-checkout upsell stays untouched.
  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target.closest && e.target.closest('[data-rendi-checkout] a');
      if (!btn || btn.hasAttribute('lb-checkout-listener')) return;
      if (window.bndlr && typeof window.bndlr.enableBundlerCheckout === 'function') {
        window.bndlr.enableBundlerCheckout();
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
